import React, { useEffect, useRef } from 'react';

import { Field, Fields, Input, SectionTitle } from '../../design-system/components/desktop';
import { useAuth } from '../../hooks/useAuth';

/**
 * The buyer's contact details.
 *
 * Converted to the library form scaffolding: `Fields` is
 * `repeat(N, minmax(0, 1fr))` upstream so a long value cannot push a track past
 * its column, and `Field` wires the label, `aria-describedby` and
 * `aria-invalid` to the control for real rather than visually.
 *
 * The self-initialisation from `useAuth` is UNCHANGED, including the
 * `initialized` ref that stops it looping. It is a real behaviour: a signed-in
 * buyer should not have to retype their own name and email.
 *
 * The section is titled "Contact details", not "Shipping Information". Nothing
 * in it is an address, and on the in-person path there is no shipping at all.
 */
const ShippingForm = ({ form, handleChange, errors = {} }) => {
  const { user } = useAuth();
  const initialized = useRef(false);

  useEffect(() => {
    if (user && !initialized.current && (!form.full_name || !form.email)) {
      const userFullName = user.full_name;
      const userEmail = user.email;

      let hasChanges = false;

      if (userFullName && form.full_name !== userFullName) {
        handleChange('full_name')({ target: { value: userFullName } });
        hasChanges = true;
      }

      if (userEmail && form.email !== userEmail) {
        handleChange('email')({ target: { value: userEmail } });
        hasChanges = true;
      }

      if (hasChanges) {
        initialized.current = true;
      }
    }
  }, [user, form, handleChange]);

  return (
    <section className="dsa-checkout__section">
      <SectionTitle>Contact details</SectionTitle>

      <Fields>
        <Field label="Full name" error={errors.full_name}>
          <Input
            block
            error={Boolean(errors.full_name)}
            placeholder="Thabo Nkosi"
            autoComplete="name"
            value={form.full_name}
            onChange={handleChange('full_name')}
          />
        </Field>

        <Field label="Phone" error={errors.phone}>
          <Input
            block
            type="tel"
            error={Boolean(errors.phone)}
            placeholder="071 234 5678"
            autoComplete="tel"
            value={form.phone}
            onChange={handleChange('phone')}
          />
        </Field>

        <Field full label="Email" error={errors.email}>
          <Input
            block
            type="email"
            error={Boolean(errors.email)}
            placeholder="thabo@sleevr.co.za"
            autoComplete="email"
            value={form.email}
            onChange={handleChange('email')}
          />
        </Field>
      </Fields>
    </section>
  );
};

export default ShippingForm;
